import type { ModuleKey } from "@/lib/modules";
import type { TenantSettings } from "@/types/tenant";

export const MOTION_PRESETS = ["none", "subtle", "immersive"] as const;

export type MotionPreset = (typeof MOTION_PRESETS)[number];
export type ModuleMotionSettings = Partial<Record<ModuleKey, MotionPreset>>;

const PATH_MODULES: ReadonlyArray<[string, ModuleKey]> = [
  ["/product-finder", "product-matching"],
  ["/therapist-match", "therapist-matching"],
  ["/therapists", "therapists"],
  ["/forecasts", "forecasts"],
  ["/podcasts", "podcasts"],
  ["/products", "products"],
  ["/gallery", "gallery"],
  ["/locations", "locations"],
  ["/booking", "booking"],
  ["/events", "events"],
  ["/forms", "forms"],
  ["/music", "music"],
  ["/radio", "radio"],
  ["/menu", "restaurant"],
  ["/blog", "blog"],
];

export function motionPreset(value: unknown): MotionPreset {
  return MOTION_PRESETS.includes(value as MotionPreset)
    ? (value as MotionPreset)
    : "subtle";
}

export function moduleMotionSettings(value: unknown): ModuleMotionSettings {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};

  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [ModuleKey, MotionPreset] =>
      MOTION_PRESETS.includes(entry[1] as MotionPreset),
    ),
  );
}

export function tenantMotionPreset(
  settings: TenantSettings | undefined,
  module?: ModuleKey,
): MotionPreset {
  const fallback = motionPreset(settings?.motionPreset);
  if (!module) return fallback;
  return moduleMotionSettings(settings?.moduleMotion)[module] ?? fallback;
}

export function moduleForPath(pathname: string, homepageModule?: ModuleKey): ModuleKey | undefined {
  if (pathname === "/") return homepageModule;
  return PATH_MODULES.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))?.[1];
}
